import { Document, Page } from "@react-pdf/renderer";
import { isNil } from "lodash";
import { useMemo } from "react";
import { formatAddressObjectFullAddress } from "../../../_src_shared/utils/functions/formatAddressObjectFullAddress";
import { formatBorrowerObjectFullName } from "../../../_src_shared/utils/functions/formatBorrowerObjectFullName";
import { formatDate } from "../../../_src_shared/utils/functions/formatDate";
import MontserratBold from "../../../assets/fonts/Montserrat/Montserrat-Bold.ttf";
import MontserratMedium from "../../../assets/fonts/Montserrat/Montserrat-Medium.ttf";
import MontserratRegular from "../../../assets/fonts/Montserrat/Montserrat-Regular.ttf";
import MontserratSemiBold from "../../../assets/fonts/Montserrat/Montserrat-SemiBold.ttf";

import MontserratBoldItalic from "../../../assets/fonts/Montserrat/Montserrat-BoldItalic.ttf";
import MontserratItalic from "../../../assets/fonts/Montserrat/Montserrat-Italic.ttf";
import internetIcon from "../../../assets/images/internet-icon.png"; // Adjust the path as necessary
import hhbHeader from "../../../assets/images/hhb-header.jpg"; // Adjust the path as necessary
import locationIcon from "../../../assets/images/location-icon.png"; // Adjust the path as necessary
import { formatDollar } from "../../../utils/numbers/formatDollar";
import { formatPercent } from "../../../utils/numbers/formatPercent";
import PdfImage from "../pdfBuilder/components/images/PdfImage";
import PdfBox from "../pdfBuilder/components/layout/PdfBox";
import PdfColumn from "../pdfBuilder/components/layout/PdfColumn";
import PdfRow from "../pdfBuilder/components/layout/PdfRow";
import PdfText from "../pdfBuilder/components/typography/PdfText";

import { Font, StyleSheet } from "@react-pdf/renderer";

Font.register({
  family: "Montserrat",
  fonts: [
    {
      src: MontserratRegular,
      fontWeight: 400,
      fontStyle: "normal",
    },
    {
      src: MontserratMedium,
      fontWeight: 500,
      fontStyle: "normal",
    },
    {
      src: MontserratSemiBold,
      fontWeight: 600,
      fontStyle: "normal",
    },
    {
      src: MontserratBold,
      fontWeight: 700,
      fontStyle: "normal",
    },
    {
      src: MontserratItalic,
      fontWeight: 400,
      fontStyle: "italic",
    },
    {
      src: MontserratBoldItalic,
      fontWeight: 700,
      fontStyle: "italic",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    fontFamily: "Montserrat",
    fontSize: 11,
  },
  // heading: {
  //   fontWeight: "bold",
  //   fontSize: 16,
  // },
  // label: {
  //   fontWeight: 600,
  // },
  // value: {
  //   fontWeight: "normal",
  // },
});

const LoanTermsSheetHhbPDF = ({ formData }) => {
  const data = useMemo(() => {
    if (formData) {
      return formData;
    }
  }, [formData]);
  return (
    <Document>
      <PageOne data={data} />
      <PageTwo data={data} />
    </Document>
  );
};

const PageOne = ({ data }) => {
  return (
    <PdfPageTemplateWrapper data={data}>
      <ContentArea style={{ paddingTop: 12, paddingBottom: 12 }}>
        <PdfColumn gap={5}>
          <TitleText>{`${data?.loanPurpose} - ${data?.loanProductType}`}</TitleText>

          {data?.subjectProperties?.map((p) => {
            return (
              <DataItem
                textStyles={{ fontSize: 13 }}
                label="Property Address"
                value={formatAddressObjectFullAddress(p.address)}
              />
            );
          })}
        </PdfColumn>
      </ContentArea>

      <ContentArea>
        <PdfRow gap={24}>
          <PdfColumn gap={9}>
            <DataItem label="Lien" value={data?.lienPosition} />
            <DataItem label="Property Type" value={data?.propertyType} />
            <DataItem
              label="Purchase Price"
              value={formatDollar(data?.purchasePrice)}
            />
            <DataItem
              label="Construction Budget"
              value={formatDollar(data?.constructionBudget)}
            />
            <DataItem
              label="Estimated As-Is Value"
              value={formatDollar(data?.asIsValue)}
            />
            <DataItem
              label="Estimated ARV"
              value={formatDollar(data?.estimatedArv)}
            />
            <DataItem label="Occupancy" value={data?.occupancy} />
            <DataItem label="Exit Strategy" value={data?.exitStrategy} />
            <DataItem
              label="Total Loan Amount"
              value={formatDollar(data?.totalLoanAmount)}
            />
            <DataItem
              label="Initial Loan Amount"
              value={formatDollar(data?.initialLoanAmount)}
            />
            <DataItem
              label="Construction Holdback"
              value={formatDollar(data?.constructionHoldback)}
            />
          </PdfColumn>

          <PdfColumn gap={9} style={{ marginLeft: 10 }}>
            <DataItem label="Loan Term" value={data?.loanTerm} />
            <DataItem
              label="Interest Rate"
              value={formatPercent(data?.interestRate)}
            />
            <DataItem label="Interest Type" value={data?.interestType} />
            <DataItem label="Amortization" value={data?.amortization} />
            <DataItem
              label="Origination Fee"
              value={formatDollar(data?.originationFee)}
            />
            <DataItem label="Legal Fee" value={formatDollar(data?.legalFee)} />
            <DataItem
              label="Underwriting Fee"
              value={formatDollar(data?.underwritingFee)}
            />
            <DataItem label="Payment Reserve" value={data?.paymentReserve} />
            <DataItem
              label="Loan to Cost"
              value={formatPercent(data?.loanToCost)}
            />
            <DataItem
              label="Loan to Value"
              value={formatPercent(data?.loanToValue)}
            />
            <DataItem
              label="Estimated ARV"
              value={formatPercent(data?.arvLtv)}
            />
          </PdfColumn>
        </PdfRow>
      </ContentArea>
      <ContentArea style={{ paddingTop: "64px", paddingBottom: "52px" }}>
        <PdfBox style={{ alignItems: "center" }}>
          <SignatureInitialsBlock />
        </PdfBox>
      </ContentArea>
      <ContentArea>
        <TitleText
          center
          style={{
            lineHeight: "13px",
            fontSize: 12,
            textAlign: "center",
            fontStyle: "italic",
          }}
        >
          [Signatures Required on Following Page]
        </TitleText>
      </ContentArea>
      <ContentArea style={{ paddingTop: 0 }}>
        <PdfText style={{ lineHeight: "13px", fontSize: 7 }}>
          This term sheet is for discussion purposes only and does not
          constitute a loan commitment, approval, or obligation to lend or
          borrow. All terms, including loan amount, are estimates only and
          remain subject to receipt of a complete application, underwriting,
          credit approval, and third-party due diligence, all in the lender’s
          sole discretion. Estimated amounts exclude third-party settlement and
          due-diligence costs, which vary by transaction and jurisdiction. No
          reliance may be placed on the information herein, and the lender may
          discontinue its review at any time without liability. Legal,
          third-party, and service fees may be required prior to or in
          connection with delivery of loan documents. Minimum profitability and
          return requirements apply.
        </PdfText>
      </ContentArea>
    </PdfPageTemplateWrapper>
  );
};
const PageTwo = ({ data }) => {
  return (
    <PdfPageTemplateWrapper data={data} isSecondaryPage={true}>
      <ContentArea>
        <PdfColumn gap={8}>
          <TitleText>Expense Agreement:</TitleText>
          <PdfText>
            By ordering these services you understand ALL of the below:
          </PdfText>
          {/* <PdfText style={{ marginTop: 8, lineHeight: "15px" }}>
            This term sheet is for discussion purposes only and does not
            constitute a loan commitment, approval, or obligation to lend or
            borrow. All terms, including loan amount, are estimates only and
            remain subject to receipt of a complete application, underwriting,
            credit approval, and third-party due diligence, all in the lender’s
            sole discretion. Estimated amounts exclude third-party settlement
            and due-diligence costs, which vary by transaction and jurisdiction.
            No reliance may be placed on the information herein, and the lender
            may discontinue its review at any time without liability. Legal,
            third-party, and service fees may be required prior to or in
            connection with delivery of loan documents. Minimum profitability
            and return requirements apply.
          </PdfText>

          <ContentArea>
            <Text>
              IN WITNESS WHEREOF, the undersigned Borrower has executed this
              Financing Estimate as of the date below.
            </Text>
            <PdfColumn gap={12} style={{ marginTop: 16 }}>
              <PdfRow style={{ justifyContent: "space-between" }}>
                {data?.guarantors?.map((g) => {
                  return (
                    <SignatureStandardBlock
                      name={`${formatBorrowerObjectFullName(g)}`}
                    />
                  );
                })}
              </PdfRow>
            </PdfColumn>
          </ContentArea> */}
          <PdfText style={{ marginTop: 0, lineHeight: "15px" }}>
            Client agrees to be personally responsible for and to guarantee
            payment of all third-party vendor expenses incurred by Lender ISAOA
            and/or any participating lender in connection with the proposed
            financing. Deposits or payments may be insufficient to cover all
            such expense and may be applied or adjusted at closing in the
            lender’s discretion. Any pricing estimates may not reflect all
            required vendor costs. Rush fees are nonrefundable, and any unused
            deposit funds may be returned at Lender ISAOA’s discretion, subject
            to a $50 processing fee per item for loans that do not close.
          </PdfText>
          <PdfText style={{ marginTop: 8, lineHeight: "15px" }}>
            If a loan does not close, Client remains responsible for all fees
            incurred, which shall be invoiced to Client. Any invoice outstanding
            for more than 10 calendar days shall accrue interest at the maximum
            rate permitted by applicable law, together with all reasonable legal
            and collection costs.
          </PdfText>
        </PdfColumn>
      </ContentArea>

      <ContentArea style={{ paddingTop: "24px" }}>
        <PdfColumn gap={16}>
          <TitleText>{data?.entityName}</TitleText>
          {/* <TitleText>GUARANTOR:</TitleText> */}
          {/* <PdfColumn gap={36}>
            {data?.guarantors?.map((g) => {
              return (
                <SignatureGuarantorBlock
                  name={`${formatBorrowerObjectFullName(g)}`}
                />
              );
            })}
          </PdfColumn> */}
          <PdfColumn gap={12} style={{ marginTop: 8 }}>
            <PdfRow
              style={{
                flexWrap: "wrap",
                justifyContent: "space-between",
                gap: 36,
              }}
            >
              {/* {b?.map((g) => {
                return (
                  <SignatureStandardBlock
                    name={`${formatBorrowerObjectFullName(g)}`}
                  />
                );
              })} */}
              {data?.guarantors?.map((g) => {
                return (
                  <SignatureStandardBlock
                    name={`${formatBorrowerObjectFullName(g)}`}
                  />
                );
              })}
            </PdfRow>
          </PdfColumn>
        </PdfColumn>
      </ContentArea>
      <ContentArea style={{ paddingTop: 0 }}></ContentArea>
    </PdfPageTemplateWrapper>
  );
};

const PdfPageTemplateWrapper = ({
  children,
  data,
  isSecondaryPage = false,
}) => {
  return (
    // <PdfPage padding={0} style={{ position: "relative" }}>
    <Page size="A4" style={styles.page}>
      <PdfImage src={hhbHeader} width={"100%"} height={120} />
      {isSecondaryPage ? (
        <PdfBox
          style={{
            flexDirection: "row",
            width: "270px",
            paddingTop: 20,
          }}
        >
          <SubHeaderRowItem
            // width={"100%"}

            textStyle={{
              color: "#ffffff",
              fontStyle: "italic",
            }}
            style={{
              paddingLeft: 54,
              backgroundColor: "#123753",
              borderTopRightRadius: 24,
              borderBottomRightRadius: 24,
            }}
          >
            Loan Details
          </SubHeaderRowItem>
        </PdfBox>
      ) : (
        <PdfBox
          style={{
            flexDirection: "row",
            width: "100%",
            paddingTop: 20,
          }}
        >
          <SubHeaderRowItem
            // width={"100%"}
            // width={150}
            center
            textStyle={{
              color: "#ffffff",
              fontStyle: "italic",
            }}
            style={{
              backgroundColor: "#123753",
              borderTopRightRadius: 24,
              borderBottomRightRadius: 24,
            }}
          >
            Loan Details
          </SubHeaderRowItem>

          <SubHeaderRowItem center style={{ flexGrow: 1 }}>
            {data?.entityName}
          </SubHeaderRowItem>

          <SubHeaderRowItem width={150} center>
            {formatDate(new Date())}
          </SubHeaderRowItem>
        </PdfBox>
      )}

      <PrimaryContentArea>{children}</PrimaryContentArea>

      <PdfFooter />
    </Page>
    // </PdfPage>
  );
};

const SubHeaderRowItem = ({
  children,
  style,
  center = false,
  width,
  textStyle,
}) => {
  return (
    <PdfBox
      style={{
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 8,
        paddingBottom: 8,
        flexGrow: 1,
        // backgroundColor: "red",
        justifyContent: "center",
        alignItems: center ? "center" : "flex-start",
        ...(width ? { width } : {}),
        ...style,
      }}
    >
      <TitleText center={center} style={textStyle}>
        {children}
      </TitleText>
    </PdfBox>
  );
};

const TitleText = ({ children, center = false, fontSize = 14, style }) => {
  return (
    <PdfText
      fontWeight={800}
      fontSize={fontSize}
      style={{
        textAlign: center ? "center" : "left",
        ...style,
      }}
    >
      {children}
    </PdfText>
  );
};

const SignatureStandardBlock = ({ name }) => {
  return (
    <PdfColumn gap={6}>
      <DataItem label="By" customValue={<SignatureBox width="185px" />} />
      <DataItem label="Name" value={name} />
      <DataItem
        label="Today's Date"
        customValue={<SignatureBox width="150px" />}
      />
    </PdfColumn>
  );
};


const SignatureInitialsBlock = ({ name }) => {
  return (
    <DataItem
      marginBottom={0}
      label="Initials"
      style={{ lineHeight: "15px", fontSize: 12 }}
      customValue={
        <PdfRow marginBottom={0}>
          <SignatureBox width="50px" />
          <SignatureBox width="50px" style={{ marginLeft: 1 }} />
        </PdfRow>
      }
    />
  );
};

const PrimaryContentArea = ({ children, style }) => {
  return (
    <PdfBox
      style={{
        width: "100%",

        paddingLeft: 28,
        paddingRight: 28,
        // paddingLeft: 32,
        // paddingRight: 32,
        // paddingLeft: 24,
        // paddingRight: 24,
        paddingTop: 16,
        paddingBottom: 16,
        ...style,
      }}
    >
      {children}
    </PdfBox>
  );
};

const ContentArea = ({ children, style }) => {
  return (
    <PdfBox
      style={{
        width: "100%",

        paddingTop: 16,
        paddingBottom: 16,

        ...style,
      }}
    >
      {children}
    </PdfBox>
  );
};

const DataItem = ({
  value,
  label,
  style,
  gap = 4,
  rowStyles,
  textStyles,
  marginBottom = 0,
  customValue,
}) => {
  const styles = useMemo(() => {
    return {
      // padding: 12,
      // textAlign: center ? "center" : "left",
      // width: "100%",
      // display: "flex",

      flexWrap: "wrap",
      ...rowStyles,
    };
  }, [rowStyles]);

  const displayedValue = useMemo(() => {
    if (isNil(value)) {
      return "-";
    }
    return value;
  }, [value]);
  return (
    <PdfRow style={styles} gap={gap} marginBottom={marginBottom}>
      <PdfText fontWeight={800} fontSize={11} style={textStyles}>
        {label}:
      </PdfText>
      {customValue ? (
        customValue
      ) : (
        <PdfText fontSize={11} style={textStyles}>
          {displayedValue}
        </PdfText>
      )}
    </PdfRow>
  );
};


const SignatureBox = ({ width = "160px", style }) => {
  return (
    <PdfBox
      style={{
        width: width,
        // height: "14px",
        // backgroundColor: "red",
        borderBottom: "1px solid #000000",
        ...style,
      }}
    />
  );
};

const PdfFooter = ({ style }) => {
  const styles = useMemo(() => {
    return {
      position: "absolute",
      bottom: 0,
      left: 0,
      // height: "40px",
      width: "100%",
      // width: "100%",
      // padding: 20,
      // color: "#666666",
      backgroundColor: "#123753",
      color: "#ffffff",
      // paddingLeft: 24,
      // paddingRight: 24,

      ...style,
    };
  }, [style]);
  return (
    <PdfBox style={styles}>
      <ContentArea style={{ padding: 16 }}>
        <PdfBox
          style={{
            display: "flex",
            flexGrow: 1,
            gap: 8,
            // backgroundColor: "purple",
            alignItems: "center",
            flexDirection: "row",
          }}
        >
          {/* <LanguageOutlined color="#ffffff" /> */}
          <PdfBox
            style={{
              display: "flex",
              flexGrow: 1,
              gap: 8,
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "row",
            }}
          >
            <PdfImage
              src={internetIcon}
              width={18}
              height={18}
              // style={style}
            />
            <PdfText style={{ color: "#ffffff" }}>
              handsomehomebuyer.com
            </PdfText>
          </PdfBox>
          <PdfBox
            style={{
              display: "flex",
              flexGrow: 1,
              gap: 8,
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "row",
            }}
          >
            <PdfImage
              src={locationIcon}
              width={18}
              height={18}
              // style={style}
            />
            <PdfText style={{ color: "#ffffff" }}>(631) 888-6963</PdfText>
          </PdfBox>
        </PdfBox>
      </ContentArea>
    </PdfBox>
  );
};

export default LoanTermsSheetHhbPDF;
